import React, { Dispatch, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Tab, Tabs } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useApprovalsContext } from 'hooks/context/useApprovalsContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ApprovalStatuses } from 'types/approvals'
import { ApprovedTransactions } from './Tables/ApprovedTransactions'
import { ExpiredTransactions } from './Tables/ExpiredTransactions'
import { PendingTransactions } from './Tables/PendingTransactions'
import { RejectedTransactions } from './Tables/RejectedTransactions'

interface ApprovalTabsProps {
  queryTab: keyof typeof ApprovalStatuses
  activeTab: string
  setActiveTab: Dispatch<React.SetStateAction<string>>
}

const ApprovalTabs = ({ queryTab, activeTab, setActiveTab }: ApprovalTabsProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { currentAccount } = useAccountsContext()
  const currentAccountReference = currentAccount?.reference || ''
  const {
    pendingApprovalDetails: { hasPendingApprovals, numberOfPendingApprovals },
  } = useApprovalsContext()

  const tabs: Tab[] = [
    {
      id: ApprovalStatuses.PENDING,
      header: t('approvals.pending'),
      badgeCount: hasPendingApprovals && numberOfPendingApprovals,
      content: <PendingTransactions />,
      onClick: () => {
        queryClient.invalidateQueries({
          queryKey: reactQueryKeys.approvalsTransactions(
            currentAccountReference,
            ApprovalStatuses.PENDING
          ),
        })
      },
    },
    {
      id: ApprovalStatuses.APPROVED,
      header: t('approvals.approved'),
      content: <ApprovedTransactions />,
    },
    {
      id: ApprovalStatuses.REJECTED,
      header: t('approvals.rejected'),
      content: <RejectedTransactions />,
    },
    {
      id: ApprovalStatuses.EXPIRED,
      header: t('approvals.expired'),
      content: <ExpiredTransactions />,
    },
  ]

  useEffect(() => {
    if (queryTab) {
      setActiveTab(queryTab)
    }
  }, [queryTab])

  return <Tabs tabs={tabs} defaultTabId={activeTab} />
}

export default ApprovalTabs
